import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { I_Building, I_Camera, I_Check, I_Msg, I_Phone, I_Pin, I_Send, I_Trash, I_X } from "./icons";
import { Ava, Pill, Spinner, YesNo } from "./ui";
import { BUDGETS, PRIORITIES, STATUSES_C, TYPES, canSanction, pStyle, sStyle } from "../constants.js";
import { logActivity } from "../lib/activity.js";
import { fmtDate, fmtDateTime, fmtINR, telHref, timeAgo, todayISO, waHref } from "../lib/format.js";
import { btnD, btnP, glass, glassDeep, glassHov, heading, input, label, monoFont } from "../styles.js";
import { sb } from "../supabase.js";

const ClientDetail = ({
    clientId,
    session,
    properties,
    visits,
    onClose,
    onChanged,
    onOpenProperty,
  }) => {
    const [c, setC] = useState(null);
    const [followups, setFollowups] = useState([]);
    const [composer, setComposer] = useState("");
    const [busy, setBusy] = useState(false),
      [saving, setSaving] = useState(false);
    const [confirmDel, setConfirmDel] = useState(false);
    const myVisits = useMemo(
      () =>
        visits
          .filter((v) => v.client_id === clientId)
          .sort((a, b) => new Date(b.visited_at) - new Date(a.visited_at)),
      [visits, clientId],
    );
    const propsById = useMemo(
      () => Object.fromEntries(properties.map((p) => [p.id, p])),
      [properties],
    );
    useEffect(() => {
      let on = true;
      (async () => {
        const { data: cd } = await sb.from("clients").select("*").eq("id", clientId).single();
        if (on) setC(cd);
        const { data: fu } = await sb
          .from("followups")
          .select("*")
          .eq("client_id", clientId)
          .order("created_at", {
            ascending: false,
          });
        if (on) setFollowups(fu || []);
      })();
      return () => {
        on = false;
      };
    }, [clientId]);
    const mayApprove = canSanction(session.role);
    const [saveError, setSaveError] = useState(null);
    // Name of whoever last sanctioned / un-sanctioned this client
    const [sanctionerName, setSanctionerName] = useState(null);
    useEffect(() => {
      const uid = c?.sanctioned_by;
      if (!uid) {
        setSanctionerName(null);
        return;
      }
      let on = true;
      sb.from("profiles")
        .select("full_name")
        .eq("id", uid)
        .single()
        .then(({ data }) => on && setSanctionerName(data?.full_name || "a power user"));
      return () => {
        on = false;
      };
    }, [c?.sanctioned_by]);
    const saveTimer = useRef(null);
    const patch = (delta, immediate = false) => {
      const next = {
        ...c,
        ...delta,
      };
      setC(next);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      const doSave = async () => {
        setSaving(true);
        // sanctioned_by / sanctioned_at are stamped by the database trigger
        const { id, created_at, updated_at, sanctioned_by, sanctioned_at, ...fields } = next;
        const { data, error } = await sb
          .from("clients")
          .update(fields)
          .eq("id", clientId)
          .select("sanctioned, sanctioned_by, sanctioned_at")
          .single();
        setSaving(false);
        if (error) {
          setSaveError(error.message);
          // put the drawer back in line with what the database actually holds
          const { data: fresh } = await sb.from("clients").select("*").eq("id", clientId).single();
          if (fresh) setC(fresh);
          return;
        }
        setSaveError(null);
        if (data) setC((cur) => (cur ? { ...cur, ...data } : cur));
        if ("sanctioned" in delta)
          logActivity(
            session.user.id,
            "updated",
            "client",
            clientId,
            delta.sanctioned ? `Sanctioned ${next.name}` : `Removed sanction for ${next.name}`,
          );
        onChanged && onChanged();
      };
      if (immediate) doSave();
      else saveTimer.current = setTimeout(doSave, 700);
    };
    const addFollowup = async () => {
      if (!composer.trim()) return;
      setBusy(true);
      const { data, error } = await sb
        .from("followups")
        .insert({
          client_id: clientId,
          body: composer.trim(),
          created_by: session.user.id,
        })
        .select()
        .single();
      if (!error) {
        setFollowups((f) => [data, ...f]);
        setComposer("");
        logActivity(session.user.id, "followup", "client", clientId, `Followed up with ${c.name}`);
        await sb
          .from("clients")
          .update({
            updated_at: new Date().toISOString(),
          })
          .eq("id", clientId);
        onChanged && onChanged();
      }
      setBusy(false);
    };
    const uploadPhoto = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      // resize to a small square thumb and embed as base64
      const img = new Image();
      const fr = new FileReader();
      fr.onload = () => {
        img.onload = () => {
          const s = 200,
            cv = document.createElement("canvas");
          cv.width = s;
          cv.height = s;
          const ctx = cv.getContext("2d");
          const r = Math.min(img.width, img.height);
          const x = (img.width - r) / 2,
            y = (img.height - r) / 2;
          ctx.drawImage(img, x, y, r, r, 0, 0, s, s);
          const dataUrl = cv.toDataURL("image/jpeg", 0.78);
          patch(
            {
              photo: dataUrl,
            },
            true,
          );
        };
        img.src = fr.result;
      };
      fr.readAsDataURL(f);
    };
    const del = async () => {
      await sb.from("clients").delete().eq("id", clientId);
      logActivity(session.user.id, "updated", "client", clientId, `Deleted client ${c.name}`);
      onChanged && onChanged();
      onClose();
    };
    if (!c)
      return (
        <React.Fragment>
          <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40" onClick={onClose} />
          <div
            className={`fixed inset-y-0 right-0 w-full md:w-[640px] z-50 ${glassDeep} flex items-center justify-center text-slate-400`}
          >
            <Spinner size={20} />
          </div>
        </React.Fragment>
      );
    const waText = `Hi ${c.name?.split(" ")[0] || ""}, this is regarding the property options we discussed.`;
    return (
      <React.Fragment>
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 animate-[fadein_.15s]"
          onClick={onClose}
        />
        <div
          className={`fixed inset-y-0 right-0 w-full md:w-[640px] z-50 ${glassDeep} overflow-y-auto animate-[slidein_.22s]`}
        >
          <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur-xl border-b border-white/[0.06] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[11px] text-slate-400">
              <span className="text-amber-300/80" style={monoFont}>
                {c.code || "Client"}
              </span>
              {saving ? (
                <span className="flex items-center gap-1">
                  <Spinner size={10} />
                  saving
                </span>
              ) : saveError ? (
                <span className="text-rose-300" title={saveError}>
                  not saved — {saveError}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-emerald-300/80">
                  <I_Check size={11} />
                  saved
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400"
            >
              <I_X size={16} />
            </button>
          </div>
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Ava name={c.name} photo={c.photo} size="lg" />
                <label
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-200 text-slate-900 flex items-center justify-center cursor-pointer ring-2 ring-slate-950"
                  title="Change photo"
                >
                  <I_Camera size={11} />
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                </label>
              </div>
              <div className="flex-1 min-w-0">
                <input
                  value={c.name || ""}
                  onChange={(e) =>
                    patch({
                      name: e.target.value,
                    })
                  }
                  placeholder="Client name"
                  className="w-full text-2xl text-slate-100 bg-transparent border-b border-white/[0.06] focus:border-amber-300/50 focus:outline-none pb-1"
                  style={heading}
                />
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Pill label={c.status} s={sStyle[c.status] || sStyle.Active} small={true} />
                  <Pill
                    label={c.priority}
                    s={{
                      ...pStyle[c.priority],
                      bg: "bg-white/[0.04]",
                      ring: "ring-white/[0.08]",
                    }}
                    small={true}
                  />
                  {c.allotted && <Pill label="Allotted" s={sStyle.Allotted} small={true} />}
                  {c.sanctioned && (
                    <Pill
                      label="Sanctioned"
                      s={{
                        bg: "bg-emerald-400/10",
                        text: "text-emerald-200",
                        dot: "bg-emerald-400",
                        ring: "ring-emerald-400/20",
                      }}
                      small={true}
                    />
                  )}
                </div>
              </div>
            </div>
            {c.phone && (
              <div className="flex gap-2">
                <a
                  href={telHref(c.phone)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-200 text-slate-900 rounded-lg text-sm font-medium"
                >
                  <I_Phone size={14} />
                  Call {c.phone}
                </a>
                <a
                  href={waHref(c.whatsapp || c.phone, waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 rounded-lg text-sm font-medium"
                >
                  <I_Msg size={14} />
                  WhatsApp
                </a>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Phone</label>
                <input
                  value={c.phone || ""}
                  onChange={(e) =>
                    patch({
                      phone: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>WhatsApp (if different)</label>
                <input
                  value={c.whatsapp || ""}
                  onChange={(e) =>
                    patch({
                      whatsapp: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Status</label>
                <select
                  value={c.status}
                  onChange={(e) =>
                    patch(
                      {
                        status: e.target.value,
                      },
                      true,
                    )
                  }
                  className={input}
                >
                  {STATUSES_C.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Priority</label>
                <select
                  value={c.priority}
                  onChange={(e) =>
                    patch(
                      {
                        priority: e.target.value,
                      },
                      true,
                    )
                  }
                  className={input}
                >
                  {PRIORITIES.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Requirement</label>
                <select
                  value={c.requirement_type || ""}
                  onChange={(e) =>
                    patch(
                      {
                        requirement_type: e.target.value,
                      },
                      true,
                    )
                  }
                  className={input}
                >
                  <option value="">—</option>
                  {TYPES.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Budget</label>
                <select
                  value={c.budget_bucket || ""}
                  onChange={(e) =>
                    patch(
                      {
                        budget_bucket: e.target.value,
                      },
                      true,
                    )
                  }
                  className={input}
                >
                  <option value="">—</option>
                  {BUDGETS.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Locality / zone preferred</label>
                <input
                  value={c.locality_pref || ""}
                  onChange={(e) =>
                    patch({
                      locality_pref: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>BHK</label>
                <input
                  value={c.bhk || ""}
                  onChange={(e) =>
                    patch({
                      bhk: e.target.value,
                    })
                  }
                  placeholder="1 BHK, 2 BHK…"
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Age</label>
                <input
                  value={c.age || ""}
                  onChange={(e) =>
                    patch({
                      age: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Fellowship</label>
                <input
                  value={c.fellowship || ""}
                  onChange={(e) =>
                    patch({
                      fellowship: e.target.value,
                    })
                  }
                  className={input}
                />
              </div>
              <div>
                <label className={label}>Sanctioned?</label>
                <YesNo
                  value={!!c.sanctioned}
                  disabled={!mayApprove}
                  title={mayApprove ? undefined : "Only a power user or admin can sanction clients"}
                  onChange={(v) =>
                    v !== !!c.sanctioned &&
                    patch(
                      {
                        sanctioned: v,
                      },
                      true,
                    )
                  }
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  {c.sanctioned_at
                    ? `${c.sanctioned ? "Sanctioned" : "Unsanctioned"} by ${sanctionerName || "…"} · ${fmtDate(c.sanctioned_at)}`
                    : c.sanctioned
                      ? "Sanctioned before approvals were tracked"
                      : mayApprove
                        ? "You can approve this client"
                        : "Power users approve this"}
                </div>
              </div>
              <div>
                <label className={label}>Allotted?</label>
                <YesNo
                  value={!!c.allotted}
                  onChange={(v) =>
                    patch(
                      {
                        allotted: v,
                      },
                      true,
                    )
                  }
                />
              </div>
              <div>
                <label className={label}>Request date</label>
                <input
                  type="date"
                  value={c.request_date || ""}
                  max={todayISO()}
                  onChange={(e) =>
                    patch(
                      {
                        request_date: e.target.value || null,
                      },
                      true,
                    )
                  }
                  className={input}
                />
              </div>
            </div>
            <div>
              <label className={label}>Other members in family</label>
              <input
                value={c.other_members || ""}
                onChange={(e) =>
                  patch({
                    other_members: e.target.value,
                  })
                }
                className={input}
              />
            </div>
            <div>
              <label className={label}>Notes & remarks</label>
              <textarea
                value={c.notes || ""}
                onChange={(e) =>
                  patch({
                    notes: e.target.value,
                  })
                }
                rows={4}
                className={`${input} resize-y`}
                placeholder="History, conversation summary, special circumstances…"
              />
            </div>
            <div>
              <h3 className="text-lg text-slate-100 mb-3 flex items-center gap-2" style={heading}>
                <I_Pin size={16} className="text-teal-300" />
                Properties visited{" "}
                <span className="text-slate-500 text-sm font-sans">· {myVisits.length}</span>
              </h3>
              {myVisits.length === 0 ? (
                <div className={`${glass} rounded-lg p-4 text-center text-sm text-slate-500`}>
                  No visits logged with this client yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {myVisits.map((v) => {
                    const p = propsById[v.property_id];
                    return (
                      <button
                        key={v.id}
                        onClick={() => p && onOpenProperty(p.id)}
                        className={`${glass} ${glassHov} w-full text-left p-3 rounded-lg flex items-center gap-3`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-white/[0.04] overflow-hidden flex items-center justify-center shrink-0">
                          {p?.cover_photo ? (
                            <img src={p.cover_photo} className="w-full h-full object-cover" />
                          ) : (
                            <I_Building size={16} className="text-slate-500" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-100 truncate">
                            {p?.title || "Property removed"}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {p?.locality || "—"} · {fmtINR(p?.price_lakhs)}
                          </div>
                          {v.note && (
                            <div className="text-[11px] text-slate-500 truncate mt-0.5 italic">
                              "{v.note}"
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[11px] text-slate-400">{fmtDate(v.visited_at)}</div>
                          {v.interested === true && (
                            <span className="inline-block mt-0.5 text-[9px] uppercase tracking-wider text-emerald-300">
                              Interested
                            </span>
                          )}
                          {v.interested === false && (
                            <span className="inline-block mt-0.5 text-[9px] uppercase tracking-wider text-stone-400">
                              Not interested
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <h3 className="text-lg text-slate-100 mb-3 flex items-center gap-2" style={heading}>
                <I_Send size={16} className="text-amber-300" />
                Follow-ups{" "}
                <span className="text-slate-500 text-sm font-sans">· {followups.length}</span>
              </h3>
              <div className="flex items-end gap-2 mb-3">
                <textarea
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  rows={1}
                  placeholder="Add a follow-up note (call, message, meeting…)"
                  className={`${input} resize-none`}
                  style={{
                    minHeight: 40,
                    maxHeight: 120,
                  }}
                />
                <button
                  onClick={addFollowup}
                  disabled={busy || !composer.trim()}
                  className={`${btnP} disabled:opacity-40 px-3 py-2.5`}
                >
                  {busy ? <Spinner size={14} /> : <I_Send size={14} />}
                </button>
              </div>
              {followups.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-2">
                  No follow-ups logged yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {followups.map((f) => (
                    <div key={f.id} className={`${glass} rounded-lg p-3`}>
                      <div className="text-sm text-slate-200 whitespace-pre-wrap">{f.body}</div>
                      <div className="text-[11px] text-slate-500 mt-1.5 flex items-center justify-between">
                        <span title={fmtDateTime(f.created_at)}>{timeAgo(f.created_at)}</span>
                        {f.created_by === session.user.id && (
                          <button
                            onClick={async () => {
                              await sb.from("followups").delete().eq("id", f.id);
                              setFollowups((x) => x.filter((y) => y.id !== f.id));
                            }}
                            className="text-slate-500 hover:text-rose-300"
                          >
                            <I_Trash size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="pt-2">
              {!confirmDel ? (
                <button onClick={() => setConfirmDel(true)} className={btnD}>
                  <I_Trash size={12} />
                  Delete client
                </button>
              ) : (
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-rose-300">Delete this client and their follow-ups?</span>
                  <button
                    onClick={() => setConfirmDel(false)}
                    className="px-2 py-0.5 rounded hover:bg-white/[0.06] text-slate-300"
                  >
                    Cancel
                  </button>
                  <button onClick={del} className="px-2 py-0.5 rounded bg-rose-600 text-white">
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

export { ClientDetail };
